/**
 * Canonical decision tables + helper derivation functions.
 *
 * These define business RULES (e.g. which profile types require a worker org,
 * which statuses block onboarding). They do NOT define the available dropdown
 * items or display text -- those come from useCodeValues (tenant-specific).
 *
 * The PROFILE_TYPES and LOBS arrays are used:
 *   - As the rule reference (codes → worker org rules, blocking, etc.)
 *   - As mock data fallback when useCodeValues is not available
 * In real mode, dropdown options come from useCodeValues and are mapped to
 * ProfileType / Lob UI types via mappers that enrich with these rules.
 */

export type ProfileTypeCode =
  | "WorkerTemp"
  | "WorkerCanadianSP"
  | "WorkerCanadianInc"
  | "WorkerSubVendor"
  | "WorkerUnitedStatesW2"
  | "WorkerUnitedStatesLLC"

export type LobCode =
  | "Regular"
  | "PermPlacement"
  | "Payroll"
  | "SubVendorPayroll"
  | "Expense"
  | "SubVendorPlacement"
  | "StatementOfWork"
  | "DirectSourcing"
  | "Consulting"
  | "CWPassThrough"
  | "TIC"
  | "VMS"
  | "OnshoreDirectHire"
  | "OnshoreContracting"
  | "ContractToHire"
  | "RecruitedPayroll"
  | "Temp"
  | "TechnicalServices"
  | "Regular2"
  | "Regular3"
  | "PermPlacement2"
  | "PermPlacement3"

export type WorkerMatchMode = "STRICT_MATCH" | "NO_MATCH_CANDIDATES"

export type WorkerCreationScenario =
  | "USE_EXISTING"
  | "ADD_PROFILE_EXISTING_CONTACT"
  | "CREATE_NEW_CONTACT_AND_PROFILE"

export type WorkerOrgScenario = "USE_EXISTING_ORG" | "CREATE_NEW_ORG"

export const DECISION_TABLES = {
  // 1) Canonical code tables (used for rule lookups and mock fallback)
  PROFILE_TYPES: [
    { id: 3, code: "WorkerTemp" as ProfileTypeCode, value: "T4 Worker" },
    { id: 4, code: "WorkerCanadianSP" as ProfileTypeCode, value: "Canadian SP" },
    { id: 5, code: "WorkerCanadianInc" as ProfileTypeCode, value: "Inc worker" },
    { id: 6, code: "WorkerSubVendor" as ProfileTypeCode, value: "Subvendor worker" },
    { id: 7, code: "WorkerUnitedStatesW2" as ProfileTypeCode, value: "W2 Worker" },
    { id: 8, code: "WorkerUnitedStatesLLC" as ProfileTypeCode, value: "US Inc Worker" },
  ] as const,

  // LOBs excluded from the onboarding form dropdown (not applicable to onboarding)
  EXCLUDED_LOBS: new Set<LobCode>(["PermPlacement", "Expense", "PermPlacement2", "PermPlacement3"]),

  // Full LOB list for rule lookups and mock fallback; UI filters to isDeleted === 0
  LOBS: [
    { id: 1, code: "Regular" as LobCode, value: "Regular", isDeleted: 0 },
    { id: 2, code: "PermPlacement" as LobCode, value: "Perm Placement", isDeleted: 0 },
    { id: 3, code: "Payroll" as LobCode, value: "Payroll", isDeleted: 0 },
    { id: 4, code: "SubVendorPayroll" as LobCode, value: "Subvendor Payroll", isDeleted: 0 },
    { id: 5, code: "Expense" as LobCode, value: "Expense", isDeleted: 0 },
    { id: 6, code: "SubVendorPlacement" as LobCode, value: "Subvendor Placement", isDeleted: 0 },
    { id: 7, code: "StatementOfWork" as LobCode, value: "Statement of Work", isDeleted: 0 },
    { id: 8, code: "DirectSourcing" as LobCode, value: "Direct Sourcing", isDeleted: 0 },
    { id: 9, code: "Consulting" as LobCode, value: "Consulting", isDeleted: 1 },
    { id: 10, code: "CWPassThrough" as LobCode, value: "CW Pass-through", isDeleted: 1 },
    { id: 11, code: "TIC" as LobCode, value: "TIC", isDeleted: 1 },
    { id: 12, code: "VMS" as LobCode, value: "VMS", isDeleted: 1 },
    { id: 13, code: "OnshoreDirectHire" as LobCode, value: "Onshore Direct Hire", isDeleted: 1 },
    { id: 14, code: "OnshoreContracting" as LobCode, value: "Onshore Contracting", isDeleted: 1 },
    { id: 15, code: "ContractToHire" as LobCode, value: "Contract to Hire", isDeleted: 1 },
    { id: 16, code: "RecruitedPayroll" as LobCode, value: "Payroll", isDeleted: 1 },
    { id: 17, code: "Temp" as LobCode, value: "A&F - Perm", isDeleted: 1 },
    { id: 24, code: "TechnicalServices" as LobCode, value: "Technical Services", isDeleted: 1 },
    { id: 25, code: "Regular2" as LobCode, value: "Regular 2", isDeleted: 1 },
    { id: 26, code: "PermPlacement2" as LobCode, value: "Perm Placement 2", isDeleted: 1 },
    { id: 27, code: "Regular3" as LobCode, value: "Regular 3", isDeleted: 1 },
    { id: 28, code: "PermPlacement3" as LobCode, value: "Perm Placement 3", isDeleted: 1 },
  ] as const,

  // 2) ATS handling
  ATS_HANDLING: {
    QUEUE_MODE: "PENDING_ONLY" as const,
    SHOW_ATS_INLINE_ALWAYS: true,
    TABLE_TAGS: {
      SHOW_NEW_WORKER_TAG_WHEN_NO_MATCH: true,
      NEW_TAG_KEY: "NEW" as const,
    },
    NO_MATCH_BEHAVIOR: "MANUAL_SELECT_OR_CREATE" as const,
  },

  // 3) Worker matching rules
  WORKER_MATCHING: {
    MATCH_IF_CANDIDATES_EXIST: true,
    STRICT_MATCH: { EMAIL: "EXACT" as const, PROFILE_TYPE: "EXACT_CODE" as const },
    FALLBACK_WHEN_NO_MATCH: {
      ALLOW_MANUAL_SELECTION: true,
      ALLOW_CREATE_NEW_WORKER: true,
    },
  },

  // 4) Worker creation scenarios
  WORKER_CREATION: {
    SCENARIOS: {
      USE_EXISTING: "USE_EXISTING" as const,
      ADD_PROFILE_EXISTING_CONTACT: "ADD_PROFILE_EXISTING_CONTACT" as const,
      CREATE_NEW_CONTACT_AND_PROFILE: "CREATE_NEW_CONTACT_AND_PROFILE" as const,
    },
    DEFAULT_WHEN_NO_MATCH: ["USE_EXISTING", "CREATE_NEW_CONTACT_AND_PROFILE"] as const,
  },

  // 5) Worker Org rules
  WORKER_ORG: {
    REQUIRED_FOR: new Set<ProfileTypeCode>([
      "WorkerCanadianInc",
      "WorkerSubVendor",
      "WorkerUnitedStatesLLC",
    ]),
    HIDDEN_FOR: new Set<ProfileTypeCode>(["WorkerTemp", "WorkerUnitedStatesW2", "WorkerCanadianSP"]),
    LOCK_IF_WORKER_HAS_ORG: true,
    ALLOW_CREATE_NEW_ORG: true,
    SCENARIOS: {
      USE_EXISTING_ORG: "USE_EXISTING_ORG" as const,
      CREATE_NEW_ORG: "CREATE_NEW_ORG" as const,
    },
  },

  // 6) Blocking rules (server authoritative later; mocked now)
  BLOCKING: {
    BLOCK_IF_NO_CLIENT_ORG: true,
    BLOCK_IF_NO_PROFILE_TYPE: true,
    BLOCK_IF_NO_WORKER: true,
    BLOCK_IF_REQUIRED_ORG_MISSING: true,
    STATUS_BLOCKING: {
      ENABLED: true,
      CONTACT_STATUS_BLOCKLIST: new Set<string>(["PendingChange", "PendingApproval"]),
      PROFILE_STATUS_BLOCKLIST: new Set<string>(["PendingChange", "PendingApproval"]),
    },
  },
} as const

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/** Resolve profile type display text from code. Uses decision table values as fallback. */
export function getProfileTypeLabel(code: ProfileTypeCode | string): string {
  return (
    DECISION_TABLES.PROFILE_TYPES.find((p) => p.code === code)?.value ?? code
  )
}

/** Get active LOBs from decision tables (mock fallback only). */
export function getActiveLobs() {
  return DECISION_TABLES.LOBS.filter((l) => l.isDeleted === 0)
}

/**
 * Derive worker match mode.
 * - If there are no candidates to match against (empty list), do NOT try matching => NO_MATCH_CANDIDATES
 * - Else STRICT_MATCH (filter by exact email + exact profile type code)
 */
export function deriveWorkerMatchMode(
  candidatesByEmail: unknown[] | null | undefined
): WorkerMatchMode {
  const count = candidatesByEmail?.length ?? 0
  return count > 0 ? "STRICT_MATCH" : "NO_MATCH_CANDIDATES"
}

/**
 * Whether to show the "NEW" tag in the table next to worker name.
 * Show NEW when there are no FBO candidates by email (meaning: nothing to match against).
 */
export function shouldShowNewWorkerTag(
  candidatesByEmail: unknown[] | null | undefined
): boolean {
  if (!DECISION_TABLES.ATS_HANDLING.TABLE_TAGS.SHOW_NEW_WORKER_TAG_WHEN_NO_MATCH)
    return false
  return deriveWorkerMatchMode(candidatesByEmail) === "NO_MATCH_CANDIDATES"
}

export function isWorkerOrgRequiredForCode(code: ProfileTypeCode | string): boolean {
  return DECISION_TABLES.WORKER_ORG.REQUIRED_FOR.has(code as ProfileTypeCode)
}

export function isWorkerOrgHiddenForCode(code: ProfileTypeCode | string): boolean {
  return DECISION_TABLES.WORKER_ORG.HIDDEN_FOR.has(code as ProfileTypeCode)
}

/**
 * Decide which creation scenario is active for the Worker setup card summary.
 */
export function deriveWorkerCreationScenario(params: {
  selectedExistingProfileId?: string | null
  selectedWorkerAction?: "ADD_PROFILE" | "CREATE_CONTACT_AND_PROFILE" | null
}): WorkerCreationScenario {
  const { selectedExistingProfileId, selectedWorkerAction } = params

  if (selectedWorkerAction === "ADD_PROFILE")
    return "ADD_PROFILE_EXISTING_CONTACT"
  if (selectedWorkerAction === "CREATE_CONTACT_AND_PROFILE")
    return "CREATE_NEW_CONTACT_AND_PROFILE"
  if (selectedExistingProfileId) return "USE_EXISTING"
  return "USE_EXISTING"
}

/**
 * Decide org scenario for summary.
 */
export function deriveWorkerOrgScenario(params: {
  selectedExistingOrgId?: string | null
  selectedOrgAction?: "CREATE_NEW_ORG" | null
  autoLockedOrgId?: string | null
}): WorkerOrgScenario {
  const { selectedOrgAction, autoLockedOrgId, selectedExistingOrgId } = params
  if (selectedOrgAction === "CREATE_NEW_ORG") return "CREATE_NEW_ORG"
  if (autoLockedOrgId || selectedExistingOrgId) return "USE_EXISTING_ORG"
  return "USE_EXISTING_ORG"
}

/**
 * Status-based blocking. If either contact or profile is in a blocked status,
 * disable creation actions and disable Start Onboarding.
 */
export function isStatusBlocked(
  contactStatus?: string | null,
  profileStatus?: string | null
): boolean {
  if (!DECISION_TABLES.BLOCKING.STATUS_BLOCKING.ENABLED) return false
  const c = contactStatus ?? ""
  const p = profileStatus ?? ""
  return (
    DECISION_TABLES.BLOCKING.STATUS_BLOCKING.CONTACT_STATUS_BLOCKLIST.has(c) ||
    DECISION_TABLES.BLOCKING.STATUS_BLOCKING.PROFILE_STATUS_BLOCKLIST.has(p)
  )
}

// ---------------------------------------------------------------------------
// Code value → UI type mappers
// ---------------------------------------------------------------------------

import type { ProfileType, Lob } from "./types"

/**
 * Map code value items to ProfileType UI types, enriching with org rules
 * from decision tables.
 *
 * @param items Array of `{ id, code, text }` — from `useCodeValues` in real
 *   mode, or omit to use decision table defaults (mock/fallback).
 */
export function mapToProfileTypes(
  items?: { id: number; code: string; text: string }[],
): ProfileType[] {
  const source = items ?? DECISION_TABLES.PROFILE_TYPES.map((p) => ({
    id: p.id,
    code: p.code,
    text: p.value,
  }))
  return source.map((cv) => ({
    id: cv.id,
    code: cv.code,
    value: cv.text,
    orgRequired: isWorkerOrgRequiredForCode(cv.code),
  }))
}

/**
 * Map code value items to Lob UI types.
 *
 * @param items Array of `{ id, code, text }` — from `useCodeValues` in real
 *   mode, or omit to use decision table defaults (mock/fallback, active only).
 */
export function mapToLobs(
  items?: { id: number; code: string; text: string }[],
): Lob[] {
  const source = items ?? getActiveLobs().map((l) => ({
    id: l.id,
    code: l.code,
    text: l.value,
  }))
  return source
    .filter((cv) => !DECISION_TABLES.EXCLUDED_LOBS.has(cv.code as LobCode))
    .map((cv) => ({
      id: cv.id,
      code: cv.code,
      value: cv.text,
    }))
}
